import { useCallback, useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useTranslation } from 'react-i18next';
import EmptyTabContent from '~valuation/pages/MyValuations/EmptyTabContent';
import ValuationStats from '~valuation/pages/MyValuations/ValuationStats';
import PortfoliosList from '~valuation/pages/MyValuations/Portfolios/PortfoliosList';
import { ValuationTypeEnum } from '~/constants/enums/valuationEnums';
import {
  useLazyGetValuationListQuery,
  useGetValuationStatsQuery,
} from '~/redux/api/valuationApiSlice';
import Pagination from '~/components/Pagination';
import ValuationItemLoader from '~valuation/pages/MyValuations/ValuationItemLoader';
import { useBreakpoint } from '~/hooks';

export default function PortfolioTabContent() {
  const { t, i18n } = useTranslation('app');
  const locale = i18n.language;
  const [
    getValuationList,
    { data: portfolioData, isLoading: isPortfolioLoading, isFetching },
  ] = useLazyGetValuationListQuery();
  const { mobileScreen } = useBreakpoint();

  const { data: statsData, isLoading: isStatesLoading } =
    useGetValuationStatsQuery({ type: ValuationTypeEnum.PORTFOLIO });

  const [page, setPage] = useState<number>(1);

  const handleGetValuationList = useCallback(
    (page: number) => {
      getValuationList({
        page,
        limit: 3,
        type: ValuationTypeEnum.PORTFOLIO,
        locale,
      });
    },
    [getValuationList, locale],
  );

  useEffect(() => {
    handleGetValuationList(1);
  }, [locale, handleGetValuationList]);

  if (isStatesLoading || isPortfolioLoading) {
    return (
      <div className="flex flex-col gap-4x">
        <Skeleton
          height={12}
          width={mobileScreen ? 200 : 700}
          borderRadius={12}
        />
        <div className="grid gap-2x md:grid-cols-3">
          {Array(3)
            .fill(0)
            .map((_, i) => (
              <Skeleton height={124} borderRadius={12} key={i} />
            ))}
        </div>
        <div className="flex flex-col gap-3x">
          {Array(2)
            .fill(0)
            .map((_, i) => (
              <ValuationItemLoader showFooter={true} key={i} />
            ))}
        </div>
      </div>
    );
  }

  if (!portfolioData?.valuations.length) {
    return (
      <EmptyTabContent
        title={t('portfolioEmptyTitle')}
        description={t('portfolioEmptyDescription')}
        icon="folder"
      />
    );
  }

  return (
    <div className="flex flex-col gap-3x md:gap-4x ">
      <p className="text-body_1 text-gray__dark_1">{t('portfolioTitle')}</p>
      <ValuationStats statsData={statsData} />
      <div className="flex flex-col gap-3x md:gap-4x ">
        <PortfoliosList
          portfolios={portfolioData?.valuations}
          isFetching={isFetching}
        />
        <Pagination
          currentPage={page}
          onPageChange={(newPage) => {
            setPage(newPage);
            handleGetValuationList?.(newPage);
          }}
          totalCount={portfolioData?.paginate.totalCount}
          limit={portfolioData?.paginate.limit}
        />
      </div>
    </div>
  );
}
